import { useState, useCallback } from 'react';
import { DocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client';
import SentryService from '@/services/sentry/Sentry.service';

interface PaginatedResponse<TItem> {
  notifications?: TItem[];
  items?: TItem[];
  total?: number;
  hasMore?: boolean;
}

interface UseInfiniteScrollOptions<TData, TVariables> {
  query: DocumentNode;
  variables?: TVariables;
  dataKey: keyof TData; // Key to extract the array from the response
  pageSize?: number;
  enabled?: boolean;
  onError?: (error: Error) => void;
  onCompleted?: (data: TData) => void;
}

interface UseInfiniteScrollResult<TItem> {
  data: TItem[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  total: number;
  refetch: () => void;
  loadMore: () => void;
  refresh: () => void;
}

export function useInfiniteScroll<
  TData,
  TVariables extends Record<string, unknown>,
  TItem,
>({
  query,
  variables = {} as TVariables,
  dataKey,
  pageSize = 20,
  enabled = true,
  onError,
  onCompleted,
}: UseInfiniteScrollOptions<
  TData,
  TVariables
>): UseInfiniteScrollResult<TItem> {
  const [allData, setAllData] = useState<TItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);

  const queryVariables = {
    ...variables,
    input: {
      ...(variables.input || {}),
      limit: pageSize,
      offset,
    },
  };

  const {
    loading,
    error,
    refetch: apolloRefetch,
  } = useQuery<TData>(query, {
    variables: queryVariables,
    skip: !enabled,
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      SentryService.captureError('Error in infinite scroll query', error);
      setLoadingMore(false);
      onError?.(error);
    },
    onCompleted: (data) => {
      const response = data[dataKey] as PaginatedResponse<TItem>;

      if (response) {
        // Explicitly extract data from known response properties
        let newItems: TItem[] = [];
        if (response.notifications) {
          newItems = response.notifications;
        } else if (response.items) {
          newItems = response.items;
        }

        // Use server-provided total or fallback to current items length
        const responseTotal = response.total ?? newItems.length;
        const responseHasMore = response.hasMore ?? false;

        if (offset === 0) {
          // Initial load or refresh
          setAllData(newItems);
        } else {
          // Loading more - append to existing data
          setAllData((prev) => [...prev, ...newItems]);
        }

        setTotal(responseTotal);
        setHasMore(responseHasMore);
      }

      setLoadingMore(false);
      onCompleted?.(data);
    },
  });

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) {
      return;
    }

    setLoadingMore(true);
    setOffset((prev) => prev + pageSize);
  }, [hasMore, loading, loadingMore, pageSize]);

  const refresh = useCallback(() => {
    setAllData([]);
    setOffset(0);
    setHasMore(true);
    setLoadingMore(false);
    setTotal(0);
    apolloRefetch();
  }, [apolloRefetch]);

  const refetch = useCallback(() => {
    apolloRefetch();
  }, [apolloRefetch]);

  // Initial loading state (not loading more)
  const isInitialLoading = loading && offset === 0;

  return {
    data: allData,
    loading: isInitialLoading,
    loadingMore,
    hasMore,
    error: error || null,
    total,
    refetch,
    loadMore,
    refresh,
  };
}

export default useInfiniteScroll;
